import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import moment from 'moment';

// reactstrap components
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardText,
  ListGroup,
  ListGroupItem,
  Spinner,
  Alert,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";

// core components
import IndexNavbar from "components/Navbars/IndexNavbar.js";
import ProfilePageHeader from "components/Headers/ProfilePageHeader.js";
import IndexFooter from "components/Footers/IndexFooter.js";

// API endpoint for fetching/updating logged-in vendor profile
const API_PROFILE_URL = "/api/profile/me";

function ProfilePage() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const navigate = useNavigate();

  // Data Fetching and Auth Check (remains the same)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setFeedback({ message: '', type: '' });
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        const response = await axios.get(API_PROFILE_URL, config);
        setProfileData(response.data);
        // Initialize editData here as well
        setEditData({
            name: response.data.name || '',
            email: response.data.email || '',
            phone: response.data.phone || '',
            businessName: response.data.businessName || '',
            description: response.data.description || '',
            marketDays: response.data.marketDays || ''
        });
      } catch (err) {
        console.error("Error fetching profile data:", err.response || err);
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
           setError("Authentication failed. Please log in again.");
           localStorage.removeItem('token');
           setTimeout(() => navigate('/login'), 2000);
        } else {
           setError("Could not load profile information. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // Add/remove body class (remains the same)
  useEffect(() => {
    document.body.classList.add("profile-page");
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    return function cleanup() {
      document.body.classList.remove("profile-page");
    };
  }, []);

  // Helper to format dates (remains the same)
  const formatDate = (dateString) => {
     const options = { year: 'numeric', month: 'long', day: 'numeric' };
     try {
       if (!dateString || !moment(dateString).isValid()) return "Invalid Date";
       return new Date(dateString).toLocaleDateString(undefined, options);
     } catch (e) {
       return "Invalid Date";
     }
   };

   // Edit Mode Handlers (remains the same)
   const handleEditToggle = () => {
       if (!isEditing) {
           setEditData({
               name: profileData?.name || '',
               email: profileData?.email || '',
               phone: profileData?.phone || '',
               businessName: profileData?.businessName || '',
               description: profileData?.description || '',
               marketDays: profileData?.marketDays || ''
           });
       }
       setIsEditing(!isEditing);
       setFeedback({ message: '', type: '' });
   };

   const handleEditChange = (e) => {
       setEditData({ ...editData, [e.target.name]: e.target.value });
   };

   // Save Changes Handler (remains the same)
   const handleSaveChanges = async (e) => {
       e.preventDefault(); // Prevent default form submission
       setIsSubmitting(true);
       setFeedback({ message: '', type: '' });
       const token = localStorage.getItem('token');

       if (!token) {
           setFeedback({ message: 'Authentication expired. Please log in again.', type: 'danger'});
           setIsSubmitting(false);
           setTimeout(() => navigate('/login'), 2000);
           return;
       }

       const updatePayload = {
           name: editData.name,
           email: editData.email,
           phone: editData.phone,
           businessName: editData.businessName,
           description: editData.description,
           marketDays: editData.marketDays
       };

       try {
           const config = { headers: { 'Authorization': `Bearer ${token}` } };
           const response = await axios.put(API_PROFILE_URL, updatePayload, config);
           setProfileData(response.data);
           setFeedback({ message: 'Profile updated successfully!', type: 'success' });
           setIsEditing(false);
       } catch (err) {
           console.error("Error updating profile:", err.response?.data || err);
           const errMsg = err.response?.data?.message || "Could not update profile.";
           setFeedback({ message: `Update failed: ${errMsg}`, type: 'danger' });
       } finally {
           setIsSubmitting(false);
       }
   };

  return (
    <>
      <IndexNavbar />
      <ProfilePageHeader />
      <br />
      <div className="main">
        <div className="section profile-content">
          <Container>
            {/* Feedback Area */}
            {feedback.message && (
                <Row>
                    <Col>
                        <Alert color={feedback.type} isOpen={!!feedback.message} toggle={() => setFeedback({ message: '', type: '' })}>
                            {feedback.message}
                        </Alert>
                    </Col>
                </Row>
            )}
            {loading && (
              <Row>
                <Col className="text-center"><Spinner color="primary" /> Loading Profile...</Col>
              </Row>
            )}
            {error && !loading && (
              <Row>
                <Col><Alert color="danger">{error}</Alert></Col>
              </Row>
            )}

            {/* Profile Content */}
            {!loading && !error && profileData && (
              <Row>
                {/* Owner Info Column */}
                <Col md="5" lg="4" className="mb-4 mb-md-0">
                  <Card>
                    <CardHeader>
                      <CardTitle tag="h4">Owner Information</CardTitle>
                    </CardHeader>
                    <CardBody>
                      {/* Conditionally render View or Edit Form for Owner Info */}
                      {isEditing ? (
                        <Form> {/* Use Form tag here if needed, but onSubmit is handled elsewhere */}
                          <FormGroup>
                            <Label for="profileName">Name</Label>
                            <Input id="profileName" name="name" value={editData.name} onChange={handleEditChange} disabled={isSubmitting} />
                          </FormGroup>
                          <FormGroup>
                            <Label for="profileEmail">Email</Label>
                            <Input id="profileEmail" name="email" type="email" value={editData.email} onChange={handleEditChange} disabled={isSubmitting}/>
                          </FormGroup>
                          <FormGroup>
                            <Label for="profilePhone">Phone</Label>
                            <Input id="profilePhone" name="phone" value={editData.phone} onChange={handleEditChange} disabled={isSubmitting} />
                          </FormGroup>
                          <p><strong>Status:</strong> {profileData.status || 'N/A'}</p>
                         </Form>
                      ) : (
                        <>
                          <p><strong>Name:</strong> {profileData.name || 'N/A'}</p>
                          <p><strong>Email:</strong> {profileData.email || 'N/A'}</p>
                          <p><strong>Phone:</strong> {profileData.phone || 'N/A'}</p>
                          <p><strong>Status:</strong> {profileData.status || 'N/A'}</p>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                {/* Vendor Info Column */}
                <Col md="7" lg="8">
                   <Card>
                     <CardHeader className="d-flex justify-content-between align-items-center">
                      <CardTitle tag="h4" className="mb-0">Vendor / Business Information</CardTitle>
                      {/* Buttons remain here, outside the conditional form part */}
                       {!isEditing && (
                           <Button className="btn-round btn-sm" color="info" outline type="button" onClick={handleEditToggle}>
                               <i className="fa fa-pencil mr-1" /> Edit Profile
                           </Button>
                       )}
                    </CardHeader>
                    <CardBody>
                      {/* Conditionally render View or Edit Form for Vendor Info */}
                      {isEditing ? (
                         <Form onSubmit={handleSaveChanges}> {/* Form only wraps editable fields + save/cancel */}
                            <FormGroup>
                                <Label for="profileBusinessName">Business Name</Label>
                                <Input id="profileBusinessName" name="businessName" value={editData.businessName} onChange={handleEditChange} disabled={isSubmitting} />
                            </FormGroup>
                             <FormGroup>
                                <Label for="profileDescription">Description</Label>
                                <Input id="profileDescription" name="description" type="textarea" rows="4" value={editData.description} onChange={handleEditChange} disabled={isSubmitting} />
                            </FormGroup>
                             <FormGroup>
                                <Label for="profileMarketDays">Market Days / Schedule Info</Label>
                                <Input id="profileMarketDays" name="marketDays" type="textarea" rows="2" value={editData.marketDays} onChange={handleEditChange} placeholder="e.g., Wednesdays & Saturdays, 9 AM - 1 PM" disabled={isSubmitting} />
                            </FormGroup>
                            {/* Save/Cancel Buttons are part of the form */}
                            <div className="text-right mt-3"> {/* Added spacing */}
                                <Button className="btn-round btn-sm mr-1" color="success" type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? <><Spinner size="sm" /> Saving...</> : 'Save Changes'}
                                </Button>
                                <Button className="btn-round btn-sm" color="info" outline type="button" onClick={handleEditToggle} disabled={isSubmitting}>
                                    Cancel
                                </Button>
                            </div>
                         </Form>
                      ) : (
                         <>
                            <h5>{profileData.businessName || 'Business Name Not Set'}</h5>
                            <CardText className="description">{profileData.description || 'No description provided.'}</CardText>
                            <hr className="my-2" />
                            <CardText><strong>Find Us at the Market:</strong><br/>{profileData.marketDays || 'Please contact vendor for schedule'}</CardText>
                         </>
                      )}
                      {/* Attending Events (Read-only) */}
                      <hr className="my-2" />
                      <h6>Special Events Attending:</h6>
                      {profileData.attendingEvents && Array.isArray(profileData.attendingEvents) && profileData.attendingEvents.length > 0 ? (
                        <ListGroup flush className="mb-0">
                          {profileData.attendingEvents.map(event => (
                            <ListGroupItem key={event._id || event} className="px-0 py-1">
                              {event.name ? `${event.name} - ${formatDate(event.date)}` : `Event ID: ${event}`}
                            </ListGroupItem>
                          ))}
                        </ListGroup>
                      ) : (
                        <p className="text-muted mb-0">No special events listed.</p>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
             {/* Message if loading finished but no data/error */}
             {!loading && !error && !profileData && (
                <Row><Col><p className="text-center text-muted">Could not load profile data.</p></Col></Row>
             )}
          </Container>
        </div>
      </div>
      <IndexFooter />
    </>
  );
}

export default ProfilePage;